import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateRole,
  CreateRoleSchema,
  type Role,
  type UpdateRole,
  UpdateRoleSchema,
} from '@servicetim/shared';
import { useCreateTenantRole, useUpdateTenantRole } from '@servicetim/ui/api';
import { Input, toast } from '@servicetim/ui/components';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { useFormSubmit } from '../common/use-form-submit';

type CreateInput = z.input<typeof CreateRoleSchema>;
type UpdateInput = z.input<typeof UpdateRoleSchema>;

export function RoleDialog({ role, onClose }: { role: Role | null; onClose: () => void }) {
  return role ? (
    <RenameRoleDialog role={role} onClose={onClose} />
  ) : (
    <CreateRoleDialog onClose={onClose} />
  );
}

function CreateRoleDialog({ onClose }: { onClose: () => void }) {
  const id = useId();
  const create = useCreateTenantRole();
  const form = useForm<CreateInput, unknown, CreateRole>({
    resolver: zodResolver(CreateRoleSchema),
    defaultValues: { key: '', name: '' },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="New role template"
      description="Tenant-wide roles are copied into every new project and can be granted in permission schemes."
      formId={`${id}-form`}
      submitLabel="Create role"
      size="sm"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (values) => {
        const created = await submit.run(() => create.mutateAsync(values));
        if (created) {
          toast.success(`Role ${created.name} created`);
          onClose();
        }
      })}
    >
      <FormField
        label="Key"
        htmlFor={`${id}-key`}
        required
        description="2–40 lowercase letters, digits or underscores; immutable. project_admin, agent and customer are reserved."
        error={errors.key?.message}
      >
        <Input
          {...form.register('key')}
          {...fieldA11y(`${id}-key`, { description: true, error: errors.key })}
          className="font-mono"
          placeholder="approver"
          autoComplete="off"
        />
      </FormField>
      <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
        <Input
          {...form.register('name')}
          {...fieldA11y(`${id}-name`, { error: errors.name })}
          maxLength={80}
          placeholder="Approver"
        />
      </FormField>
    </FormDialog>
  );
}

function RenameRoleDialog({ role, onClose }: { role: Role; onClose: () => void }) {
  const id = useId();
  const update = useUpdateTenantRole();
  const form = useForm<UpdateInput, unknown, UpdateRole>({
    resolver: zodResolver(UpdateRoleSchema),
    defaultValues: { name: role.name },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={`Rename ${role.name}`}
      description={
        <span>
          Key <code className="rounded bg-surface-sunken px-1 font-mono text-xs">{role.key}</code>{' '}
          stays.
        </span>
      }
      formId={`${id}-form`}
      size="sm"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (patch) => {
        const saved = await submit.run(() => update.mutateAsync({ id: role.id, patch }));
        if (saved) {
          toast.success('Role renamed');
          onClose();
        }
      })}
    >
      <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
        <Input
          {...form.register('name')}
          {...fieldA11y(`${id}-name`, { error: errors.name })}
          maxLength={80}
        />
      </FormField>
    </FormDialog>
  );
}
